import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import { DateRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Table from '@/components/UI/Table';
import { _isEmpty, _isNotEmpty, safelyDecodeURIComponent } from '@/littledash';
import { type ID } from '@/model/Common.model';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { type TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';

interface ProjectProps {
  projectId: ID;
}

interface ProjectMetadata {
  title: string;
  value: string | Array<string>;
  field_type: string;
}

interface ProjectRole {
  user: { name: string };
  role: { name: string };
}

interface ProjectData {
  name: string;
  metadata: Array<ProjectMetadata>;
  roles: Array<ProjectRole>;
}

const Project: FC<ProjectProps> = ({ projectId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(false);
  const [data, setData] = useState<Partial<ProjectData>>({});

  const metaColumns: Array<TanStackColumnDef<ProjectMetadata>> = [
    {
      id: 'title',
      header: 'Name',
      accessorKey: 'title',
      enableSorting: true,
    },
    {
      id: 'value',
      header: 'Value',
      accessorKey: 'value',
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: ProjectMetadata } }) => {
        if (original.field_type === 'date') {
          return <DateRenderer value={original.value as string} defaultResponse="-" />;
        }
        if (Array.isArray(original.value)) {
          return original.value.map((v) => safelyDecodeURIComponent(v)).join(', ');
        }
        return original.value;
      },
    },
  ];

  const rolesColumns: Array<TanStackColumnDef<ProjectRole>> = [
    {
      id: 'user.name',
      header: 'Name',
      accessorFn: (row: ProjectRole) => row.user?.name,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: ProjectRole } }) => original.user?.name,
    },
    {
      id: 'role.name',
      header: 'Role',
      accessorFn: (row: ProjectRole) => row.role?.name,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: ProjectRole } }) => original.role?.name,
    },
  ];

  const fetchProject = async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await Http.get(apiRoute('projects.get', { projectId }));
      setData(data);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProject();
  }, []);

  if (loading || _isEmpty(data)) {
    return <Loading />;
  }

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
      {error && (
        <ApiErrorBanner
          className="mb4 mw6"
          title="There was an error retrieving project details"
          text="An error has occurred retrieving project information, please try again later. If this keeps occurring please contact support."
          error={error}
        />
      )}
      <h2 className="pb3">{data.name}</h2>
      {_isNotEmpty(data.metadata) && (
        <>
          <div className="ui-card">
            <Table data={data.metadata} columns={metaColumns} />
          </div>
        </>
      )}
      {_isNotEmpty(data.roles) && (
        <>
          <h3 className="pb3 pt4 f5 lh-title">Project roles</h3>
          <div className="ui-card">
            <Table data={data.roles} columns={rolesColumns} />
          </div>
        </>
      )}
    </>
  );
};

export default Project;
